import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  ASK_USER_TOOL_NAME,
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import { getUserMessageDisplayText } from '../utils/userMessageDisplayText';
import {
  type TracePhase,
  isHiddenToolName,
  usesReadToolCard,
  usesStandardToolCard,
} from './utils/trace-tool-filters';
import {
  endToolConclusion,
  isEndToolName,
  lastEndToolPartRef,
} from './utils/end-tool-content';
import { isHttpExchangeTool, isMcpToolName } from './utils/tool-config.helpers';
import { shouldIncludeCoPilotReadFileToolCall } from './utils/read-file-tool';
import {
  isCoderToolNameForMerge,
  mergeCoderToolOutputStrings,
} from './utils/coder-tool-output';

export type TraceToolStatus = 'started' | 'in-progress' | 'completed' | 'error';
export type TraceFileMetadata = {
  codeWithSecretsPath?: string;
  codeResultPath?: string;
  intelligenceResponsePath?: string;
  filePaths?: string[];
};
export type PlanTodoItem = {
  id: string;
  content: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
};
export type TraceToolCall = {
  id: string;
  name: string;
  args: Record<string, unknown>;
  status: TraceToolStatus;
  output?: string;
  streamMessage?: string;
  error?: string;
  timing?: {
    durationMs: number;
  };
  fileMetadata?: TraceFileMetadata;
  metadata?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    model?: string;
    fullPlan?: PlanTodoItem[];
    codeBlocked?: string;
    errorType?: string;
    errorLabel?: string;
    [key: string]: unknown;
  };
};
export type TraceMessageRole = 'user' | 'assistant' | 'tool';
export type TraceTextContent = {
  type: 'text';
  text: string;
};
export type TraceReasoningContent = {
  type: 'reasoning';
  text: string;
};
export type TraceToolCallContent = {
  type: 'tool_call';
  toolCall: TraceToolCall;
};
export type TraceToolResultContent = {
  type: 'tool_result';
  toolCallId: string;
  toolName?: string;
  content: string;
  status: 'success' | 'error';
};
export type TraceMessageContent =
  | TraceTextContent
  | TraceReasoningContent
  | TraceToolCallContent
  | TraceToolResultContent;
export type TraceMessage = {
  id: string;
  role: TraceMessageRole;
  content: TraceMessageContent[];
  timestamp?: string;
  agentName?: string;
};

function appendTextContent(contents: TraceMessageContent[], text: string) {
  const previous = contents[contents.length - 1];

  if (previous?.type === 'text') {
    previous.text += text;

    return;
  }

  contents.push({ type: 'text', text });
}

export type TraceFile = {
  id: string;
  name: string;
  content: string;
  size: number;
  createdAt: string;
};
const SAFE_CODER_ERROR_MESSAGE = 'An error occurred during python execution.';

export function parseTraceMessages(
  traceMessages: LangchainTraceMessage[],
): TraceMessage[] {
  const messages: TraceMessage[] = [];
  const toolCallOutputs = new Map<string, string>();

  for (const traceMessage of traceMessages) {
    const messageType = traceMessage.message?.kwargs?.id?.[2] as
      | string
      | undefined;

    if (messageType === 'ToolMessage') {
      const toolCallId =
        traceMessage.message?.kwargs?.tool_call_id ||
        traceMessage.message?.kwargs?.toolCallId;
      const content = traceMessage.message?.kwargs?.content;

      if (toolCallId && typeof content === 'string') {
        toolCallOutputs.set(toolCallId, content);
      }
    }
  }

  for (const traceMessage of traceMessages) {
    const kwargs = traceMessage.message?.kwargs;

    if (!kwargs) continue;
    const messageType = traceMessage.message?.id?.[2] as string | undefined;
    const messageId = traceMessage.id || String(Date.now());

    switch (messageType) {
      case 'HumanMessage': {
        const content =
          typeof kwargs.content === 'string' ? kwargs.content : '';

        if (content) {
          messages.push({
            id: messageId,
            role: 'user',
            content: [{ type: 'text', text: content }],
            timestamp: traceMessage.createdAt,
          });
        }

        break;
      }

      case 'AIMessage': {
        const contents: TraceMessageContent[] = [];

        if (Array.isArray(kwargs.content)) {
          for (const item of kwargs.content) {
            const reasoningText = extractContentItemReasoning(item);

            if (reasoningText) {
              contents.push({ type: 'reasoning', text: reasoningText });
              continue;
            }

            if (item.type === 'text' && item.text) {
              contents.push({ type: 'text', text: item.text });
            }
          }
        } else if (typeof kwargs.content === 'string' && kwargs.content) {
          contents.push({ type: 'text', text: kwargs.content });
        }

        if (Array.isArray(kwargs.tool_calls)) {
          for (const toolCall of kwargs.tool_calls) {
            const output = toolCallOutputs.get(toolCall.id);

            contents.push({
              type: 'tool_call',
              toolCall: {
                id: toolCall.id,
                name: toolCall.name,
                args: toolCall.args || {},
                status: output ? 'completed' : 'started',
                output,
              },
            });
          }
        }

        if (contents.length > 0) {
          messages.push({
            id: messageId,
            role: 'assistant',
            content: contents,
            timestamp: traceMessage.createdAt,
          });
        }

        break;
      }
    }
  }

  return messages;
}

export {
  type TracePhase,
  isHiddenToolName,
  usesReadToolCard,
  usesStandardToolCard,
};

function getToolNameVariants(toolName: string): string[] {
  const variants = new Set([toolName]);

  if (toolName.includes('-')) {
    variants.add(toolName.replace(/-/g, '_'));
  }

  return Array.from(variants);
}

const BASE_KNOWN_TOOL_NAMES = [
  'ask-codebase',
  'ask-code-base-tool',
  'application-scoping-tool',
  'authenticate',
  'authentication-details',
  'authentication-details-tool',
  'business-context-tool',
  'get-business-context',
  'code-analyzer-tool',
  'coder',
  'coder-tool',
  'dynamic-http-tool',
  'enterprise-search',
  'endpoint-details',
  'endpoint-details-tool',
  'endpoint-read',
  'get-tracked-endpoint-schema',
  'search-tracked-endpoints',
  'end-test',
  'findings-tool',
  'file-upload-client',
  'get-authentication-details',
  'get-mcp-authentication-details',
  'mcp-authentication-details',
  'mcp-connection-status-tool',
  'mcp-test-connection-tool',
  'get-endpoint-details',
  'grep',
  'http-tool',
  'intelligence',
  'read-emails',
  'tag-emails',
  'save-relevant-emails',
  'signal-latest-execution-summary-tool',
  'signals-semantic-search-tool',
  'list-assets-tool',
  'list-files',
  'long-term-memory-tool',
  'ls',
  'navigate-web',
  'out-of-band-detection',
  'prepare-file',
  'python',
  'python-tool',
  'read-file',
  'search-endpoints',
  'signals-semantic-search-tool',
  'sse-client',
  'sub-agent-coder',
  'sub-agent-intelligence',
  'todo-write',
  'websearch-tool',
  'websocket-client',
  'write-file',
  'end',
  'get-neo-memories',
  'signals-explorer-tool',
  'findings-explorer-tool',
  'endpoints-explorer-tool',
  'executions-explorer-tool',
  'har-explorer-tool',
  'cli-tool',
  'login-tool',
  'ai-discovery-http',
  'ai-discovery-fetch-openapi',
  'ai-discovery-sse',
  'ai-discovery-websocket',
  'ai-discovery-complete',
  'ai-discovery-verdict',
  'ai-discovery-mcp',
  'ai-discovery-update-profile',
  'ai-discovery-record-component',
  'remote-c2-command',
  'password-audit-crack',
  'list-ad-entities',
  'get-ad-entity-details',
  'get-ad-entity-relationships',
  'query-ad-graph',
  'create-internal-network-discovery-signal',
  'get-existing-internal-network-discovery-signals',
  'create-internal-network-scenario-draft',
];
const KNOWN_TOOL_NAMES = new Set(
  BASE_KNOWN_TOOL_NAMES.flatMap((toolName) => getToolNameVariants(toolName)),
);

function normalizeToolNameForLookup(toolName: string): string {
  return toolName.toLowerCase().replace(/_/g, '-');
}

export function isKnownToolName(toolName: string): boolean {
  const normalized = normalizeToolNameForLookup(toolName);

  return KNOWN_TOOL_NAMES.has(normalized);
}

export function shouldShowToolHarSection(toolName: string): boolean {
  if (!toolName) {
    return false;
  }

  const normalized = normalizeToolNameForLookup(toolName);

  if (
    normalized === 'python' ||
    normalized === 'python-tool' ||
    normalized === 'file-upload-client'
  ) {
    return true;
  }

  if (isHttpExchangeTool(toolName) || isMcpToolName(toolName)) {
    return true;
  }

  return !isKnownToolName(toolName);
}

function shouldParseToolCall(toolName: string, phase: TracePhase): boolean {
  if (!toolName || toolName === 'tool') {
    return false;
  }

  if (isHiddenToolName(toolName, phase)) {
    return false;
  }

  if (
    phase === 'ai-discovery' ||
    phase === 'co-pilot' ||
    phase === 'attack' ||
    phase === 'post-attack'
  ) {
    return true;
  }

  if (isMcpToolName(toolName)) {
    return true;
  }

  return isKnownToolName(toolName);
}

export function isRenderableToolName(
  toolName: string,
  phase: TracePhase = 'attack',
): boolean {
  if (!toolName || toolName === 'tool') {
    return false;
  }

  if (isMcpToolName(toolName)) {
    return true;
  }

  return shouldParseToolCall(toolName, phase);
}

export { isMcpToolName } from './utils/tool-config.helpers';

function stringifyToolOutputForTrace(output: unknown): string | undefined {
  if (typeof output === 'string') {
    return output;
  }

  if (output !== undefined && output !== null) {
    return JSON.stringify(output);
  }

  return undefined;
}

function isCoderToolName(toolName: string): boolean {
  const normalized = toolName.toLowerCase().replace(/_/g, '-');

  return (
    normalized === 'coder' ||
    normalized === 'sub-agent-coder' ||
    normalized === 'coder-tool' ||
    normalized === 'python' ||
    normalized === 'python-tool'
  );
}

function extractAgentNameFromMetadata(metadata: unknown): string | undefined {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  const metadataRecord = metadata as { agentName?: unknown };

  return typeof metadataRecord.agentName === 'string'
    ? metadataRecord.agentName
    : undefined;
}

function extractMessageAgentName(
  historyMessage: TerraUIMessage,
): string | undefined {
  return extractAgentNameFromMetadata(
    (historyMessage as { metadata?: unknown }).metadata,
  );
}

type ToolCallContentPosition = {
  messageIndex: number;
  contentIndex: number;
};

function preferMergedToolOutput(
  earlier?: string,
  later?: string,
): string | undefined {
  if (later?.trim()) {
    return later;
  }

  if (earlier?.trim()) {
    return earlier;
  }

  return later ?? earlier;
}

function mergeTraceToolCallsForHistory(
  earlier: TraceToolCall,
  later: TraceToolCall,
): TraceToolCall {
  const laterArgCount = Object.keys(later.args ?? {}).length;
  const mergedOutput = isCoderToolNameForMerge(later.name)
    ? mergeCoderToolOutputStrings(earlier.output, later.output)
    : preferMergedToolOutput(earlier.output, later.output);

  return {
    id: later.id,
    name: later.name,
    args: laterArgCount > 0 ? later.args : earlier.args,
    status: later.status,
    output: mergedOutput,
    streamMessage: later.streamMessage ?? earlier.streamMessage,
    error: later.error ?? earlier.error,
    timing: later.timing ?? earlier.timing,
    fileMetadata: later.fileMetadata ?? earlier.fileMetadata,
    metadata: { ...earlier.metadata, ...later.metadata },
  };
}

function dedupeAssistantToolCallsByToolCallId(
  messages: TraceMessage[],
): TraceMessage[] {
  const mergedByOccurrence = new Map<string, TraceToolCall>();
  const lastPositionByOccurrence = new Map<string, ToolCallContentPosition>();
  const occurrenceByPosition = new Map<string, string>();
  const latestOccurrenceById = new Map<
    string,
    { key: string; lastMessageIndex: number }
  >();
  const occurrenceCountById = new Map<string, number>();

  for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
    const message = messages[messageIndex];

    if (message.role !== 'assistant') {
      continue;
    }

    message.content.forEach((content, contentIndex) => {
      if (content.type !== 'tool_call') {
        return;
      }

      const { id } = content.toolCall;

      if (!id) {
        return;
      }

      const latestOccurrence = latestOccurrenceById.get(id);
      const latestToolCall = latestOccurrence
        ? mergedByOccurrence.get(latestOccurrence.key)
        : undefined;
      const latestIsTerminal =
        latestToolCall?.status === 'completed' ||
        latestToolCall?.status === 'error';
      const startsNewOccurrence =
        latestOccurrence !== undefined &&
        latestIsTerminal &&
        latestOccurrence.lastMessageIndex !== messageIndex;
      const occurrenceCount = startsNewOccurrence
        ? (occurrenceCountById.get(id) ?? 0) + 1
        : (occurrenceCountById.get(id) ?? 0);
      const occurrenceKey =
        !latestOccurrence || startsNewOccurrence
          ? `${id}:${occurrenceCount}`
          : latestOccurrence.key;
      const prior = mergedByOccurrence.get(occurrenceKey);

      occurrenceCountById.set(id, occurrenceCount);
      mergedByOccurrence.set(
        occurrenceKey,
        prior
          ? mergeTraceToolCallsForHistory(prior, content.toolCall)
          : content.toolCall,
      );
      lastPositionByOccurrence.set(occurrenceKey, {
        messageIndex,
        contentIndex,
      });
      latestOccurrenceById.set(id, {
        key: occurrenceKey,
        lastMessageIndex: messageIndex,
      });
      occurrenceByPosition.set(
        `${messageIndex}:${contentIndex}`,
        occurrenceKey,
      );
    });
  }

  const rebuilt = messages.map((message, messageIndex) => {
    if (message.role !== 'assistant') {
      return message;
    }

    const nextContent: TraceMessageContent[] = [];

    for (
      let contentIndex = 0;
      contentIndex < message.content.length;
      contentIndex++
    ) {
      const content = message.content[contentIndex];

      if (content.type !== 'tool_call') {
        nextContent.push(content);
        continue;
      }

      const { id } = content.toolCall;

      if (!id) {
        nextContent.push(content);
        continue;
      }

      const occurrenceKey = occurrenceByPosition.get(
        `${messageIndex}:${contentIndex}`,
      );
      const lastPos =
        occurrenceKey === undefined
          ? undefined
          : lastPositionByOccurrence.get(occurrenceKey);
      const merged =
        occurrenceKey === undefined
          ? undefined
          : mergedByOccurrence.get(occurrenceKey);

      if (
        !lastPos ||
        !merged ||
        lastPos.messageIndex !== messageIndex ||
        lastPos.contentIndex !== contentIndex
      ) {
        continue;
      }

      nextContent.push({ type: 'tool_call', toolCall: merged });
    }

    return { ...message, content: nextContent };
  });

  return rebuilt.filter(
    (message) => message.role !== 'assistant' || message.content.length > 0,
  );
}

function isAskUserConfirmationRecord(value: unknown): value is {
  type: ConfirmationMessageTypeEnum.UserConfirmation;
  toolName: string;
} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as {
    type?: unknown;
    toolName?: unknown;
  };

  return (
    record.type === ConfirmationMessageTypeEnum.UserConfirmation &&
    record.toolName === ASK_USER_TOOL_NAME
  );
}

function isAskUserConfirmationUserMessage(
  historyMessage: TerraUIMessage,
): boolean {
  if (isAskUserConfirmationRecord(historyMessage.metadata)) {
    return true;
  }

  for (const part of historyMessage.parts ?? []) {
    if (part.type !== TerraUIMessageDataPartName || !part.data) {
      continue;
    }

    if (isAskUserConfirmationRecord(part.data)) {
      return true;
    }
  }

  return false;
}

export function parseChatHistory(
  history: TerraUIMessage[],
  phase: TracePhase = 'attack',
): TraceMessage[] {
  const messages: TraceMessage[] = [];
  const toolCallOutputs = new Map<
    string,
    { output: string; toolName: string }
  >();

  for (const historyMessage of history) {
    if (historyMessage.role === 'assistant') {
      for (const part of historyMessage.parts || []) {
        if (
          part.type === 'data-customData' &&
          (part.data?.type === DataMessageTypeEnum.ToolCallComplete ||
            (part.data?.type === DataMessageTypeEnum.ToolCallUpdate &&
              isCoderToolNameForMerge(part.data.toolName)))
        ) {
          const data = part.data;

          if (data.toolCallId) {
            const output = stringifyToolOutputForTrace(data.output);

            if (output) {
              const existing = toolCallOutputs.get(data.toolCallId);
              const mergedOutput =
                existing && isCoderToolNameForMerge(data.toolName)
                  ? mergeCoderToolOutputStrings(existing.output, output)
                  : output;

              toolCallOutputs.set(data.toolCallId, {
                output: mergedOutput ?? output,
                toolName: data.toolName || 'tool',
              });
            }
          }
        }
      }
    }
  }

  const lastEndTool = lastEndToolPartRef(history);

  for (let messageIndex = 0; messageIndex < history.length; messageIndex++) {
    const historyMessage = history[messageIndex];
    const messageId = historyMessage.id || `trace-msg-${messageIndex}`;

    if (historyMessage.role === 'user') {
      if (isAskUserConfirmationUserMessage(historyMessage)) {
        continue;
      }

      const displayText = getUserMessageDisplayText({
        message: historyMessage,
      }).trim();

      if (displayText.length > 0) {
        messages.push({
          id: messageId,
          role: 'user',
          content: [{ type: 'text', text: displayText }],
        });
      }
    } else if (historyMessage.role === 'assistant') {
      const contents: TraceMessageContent[] = [];
      const parts = historyMessage.parts || [];

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];

        if (part.type === 'text' && part.text) {
          const rawText = part.text;
          const trimmedText = rawText.trim();

          if (!trimmedText) {
            const previous = contents[contents.length - 1];

            if (previous?.type === 'text') {
              previous.text += rawText;
            }

            continue;
          }

          appendTextContent(contents, rawText);
        } else if (part.type === 'data-customData' && part.data) {
          const data = part.data;

          if (data.type === DataMessageTypeEnum.SmokeReasoning) {
            const reasoningText =
              typeof data.text === 'string' ? data.text.trim() : '';

            if (reasoningText) {
              contents.push({ type: 'reasoning', text: reasoningText });
            }

            continue;
          }

          if (
            data.type === DataMessageTypeEnum.ToolCallComplete &&
            data.toolName &&
            isEndToolName(data.toolName)
          ) {
            const conclusion = endToolConclusion(data.input);
            const isLastEndTool =
              lastEndTool?.messageIndex === messageIndex &&
              lastEndTool.partIndex === i;

            if (isLastEndTool && conclusion) {
              appendTextContent(contents, conclusion);
            }

            continue;
          }

          if (
            (data.type === DataMessageTypeEnum.ToolCallComplete ||
              ((data.type === DataMessageTypeEnum.ToolCallStart ||
                data.type === DataMessageTypeEnum.ToolCallUpdate) &&
                (data.toolName === 'authenticate' ||
                  data.toolName === 'python'))) &&
            data.toolName &&
            data.toolCallId &&
            shouldParseToolCall(data.toolName, phase) &&
            !shouldOmitCoPilotTraceToolCall({
              phase,
              toolName: data.toolName,
              input: data.input,
              output: data.output,
            })
          ) {
            const isErrorStatus = data.status === 'error';
            const isCoderTool = isCoderToolName(data.toolName);
            const output = toolCallOutputs.get(data.toolCallId);
            const matchingToolOutput =
              output?.toolName === data.toolName ? output.output : undefined;
            const fileMetadata = data.fileMetadata as
              | TraceFileMetadata
              | undefined;
            const metadata = data.metadata as TraceToolCall['metadata'];
            const metadataErrorMessage =
              typeof metadata?.errorMessage === 'string' &&
              metadata.errorMessage.trim().length > 0
                ? metadata.errorMessage
                : undefined;

            contents.push({
              type: 'tool_call',
              toolCall: {
                id: data.toolCallId || part.id || '',
                name: data.toolName,
                args: (data.input &&
                typeof data.input === 'object' &&
                !Array.isArray(data.input)
                  ? (data.input as Record<string, unknown>)
                  : {}) as Record<string, unknown>,
                status: isErrorStatus
                  ? 'error'
                  : data.type === DataMessageTypeEnum.ToolCallStart
                    ? 'started'
                    : data.type === DataMessageTypeEnum.ToolCallUpdate
                      ? 'in-progress'
                      : 'completed',
                output:
                  matchingToolOutput ||
                  stringifyToolOutputForTrace(data.output) ||
                  (typeof data.message === 'string' &&
                  !isCoderToolName(data.toolName)
                    ? data.message
                    : undefined),
                streamMessage:
                  typeof data.message === 'string' ? data.message : undefined,
                error:
                  isErrorStatus && isCoderTool
                    ? metadataErrorMessage || SAFE_CODER_ERROR_MESSAGE
                    : data.error ||
                      (isErrorStatus
                        ? typeof data.message === 'string'
                          ? data.message
                          : typeof data.output === 'string'
                            ? data.output
                            : undefined
                        : undefined),
                fileMetadata,
                metadata,
                ...(data.timing?.durationMs !== undefined
                  ? { timing: { durationMs: data.timing.durationMs } }
                  : {}),
              },
            });
          }
        }
      }

      if (contents.length > 0) {
        messages.push({
          id: messageId,
          role: 'assistant',
          content: contents,
          ...(extractMessageAgentName(historyMessage)
            ? { agentName: extractMessageAgentName(historyMessage) }
            : {}),
        });
      }
    }
  }

  const deduped = dedupeAssistantToolCallsByToolCallId(messages);

  if (phase === 'co-pilot') {
    return normalizeCoPilotTraceMessages(deduped);
  }

  return deduped;
}

function shouldOmitCoPilotTraceToolCall({
  phase,
  toolName,
  input,
  output,
}: {
  phase: TracePhase;
  toolName: string;
  input?: unknown;
  output?: unknown;
}): boolean {
  if (phase !== 'co-pilot') {
    return false;
  }

  const normalized = toolName.toLowerCase().replace(/_/g, '-');

  if (normalized === 'read-file' || normalized === 'read_file') {
    return !shouldIncludeCoPilotReadFileToolCall({ input, output });
  }

  return false;
}

function normalizeCoPilotTraceMessages(
  messages: TraceMessage[],
): TraceMessage[] {
  return messages.map((message) => {
    if (message.role !== 'assistant') {
      return message;
    }

    const nextContent: TraceMessageContent[] = [];

    for (const content of message.content) {
      if (content.type === 'reasoning') {
        continue;
      }

      if (content.type === 'text') {
        const previous = nextContent[nextContent.length - 1];

        if (previous?.type === 'text') {
          previous.text += content.text;
          continue;
        }
      }

      nextContent.push(content);
    }

    return { ...message, content: nextContent };
  });
}

export type LangchainTraceMessage = {
  id: string;
  tenantId?: string;
  applicationId?: string;
  threadId?: string;
  message?: {
    id?: string[];
    kwargs?: {
      content?: string | ContentItem[];
      tool_call_id?: string;
      toolCallId?: string;
      tool_calls?: ToolCallItem[];
      id?: string[];
    };
  };
  createdAt?: string;
};
type ContentItem = {
  type: string;
  text?: string;
  thought?: boolean;
  reasoning?: string;
  thinking?: string;
  reasoningText?: {
    text?: string;
    signature?: string;
  };
};

function extractContentItemReasoning(item: ContentItem): string {
  if (item.type === 'text' && item.thought === true && item.text) {
    return item.text;
  }

  if (item.type === 'reasoning_content') {
    return item.reasoningText?.text || item.text || '';
  }

  if (item.type === 'reasoning') {
    return item.reasoning || item.text || '';
  }

  if (item.type === 'thinking') {
    return item.thinking || item.reasoning || item.text || '';
  }

  return '';
}

type ToolCallItem = {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  type?: string;
};
